import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import AppStoreBadge from '@deliveroo/consumer-component-library/components/AppStoreBadge';
import Button from '@deliveroo/consumer-component-library/components/Button';
import P from '@deliveroo/consumer-component-library/components/P';
import BackgroundImage from '@deliveroo/consumer-component-library/components/BackgroundImage';

import { trackClickAppDownload } from '../../actions/tracking/apps';
import { numberToCurrency } from '../../utils/currency';
import { showInviteModal } from '../../actions/inviteActions';
import i18n from '../../utils/i18n';
import { Container } from '../../atoms';

import mgmImage from './images/mgm.svg';
import forBusinessImage from './images/business.jpg';
import appsImage from './images/apps.svg';

import styles from './styles.scss';

export const HomepageMarketingContent = ({
  config,
  currentCountry,
  rooviteLink,
  clickInviteLink,
  locale,
  clickAppDownload,
}) => {
  const rooviteConfig = config.countries.find((country) => country.id === currentCountry.tld)
    .roovite;
  const referrerAmount = numberToCurrency(
    rooviteConfig.referrerAmount,
    locale,
    rooviteConfig.currencySymbol,
    rooviteConfig.currencyCode,
  );
  const inviteeAmount = numberToCurrency(
    rooviteConfig.inviteeAmount,
    locale,
    rooviteConfig.currencySymbol,
    rooviteConfig.currencyCode,
  );

  return (
    <div className={styles.wrapper}>
      <Container>
        <h2 className={styles.title}>{i18n.gettext('Fresh from the kitchen')}</h2>
        <ul className={styles.list}>
          {rooviteLink && (
            <li className={styles.panel}>
              <div className={styles['panel-image-container']}>
                <BackgroundImage src={mgmImage} />
              </div>
              <div className={styles['panel-content']}>
                <h3 className={styles['panel-title']}>{i18n.gettext('Get free food')}</h3>
                <P>
                  {i18n.gettext(
                    'Give %(inviteeAmount)s, get %(referrerAmount)s - invite friends and get dinner on us.',
                    { inviteeAmount, referrerAmount },
                  )}
                </P>
                <p className={styles.buttons}>
                  <Button onClick={clickInviteLink}>{i18n.gettext('Share your code')}</Button>
                </p>
              </div>
            </li>
          )}

          {currentCountry.enableDeliverooForBusiness && (
            <li className={classnames(styles.panel, styles['panel-business'])}>
              <div className={styles['panel-image-container']}>
                <BackgroundImage src={forBusinessImage} />
              </div>
              <div className={styles['panel-content']}>
                <h3 className={styles['panel-title']}>{i18n.gettext('Deliveroo for Business')}</h3>
                <P>
                  {i18n.gettext('Hungry clients or hungry staff, our corporate team can help.')}
                </P>
                <p className={styles.buttons}>
                  <Button href="/business">{i18n.gettext('Enquire')}</Button>
                </p>
              </div>
            </li>
          )}

          <li className={classnames(styles.panel, styles['panel-apps'])}>
            <div className={styles['panel-image-container']}>
              <BackgroundImage src={appsImage} />
            </div>
            <div className={styles['panel-content']}>
              <h3 className={styles['panel-title']}>{i18n.gettext('Have you got the app?')}</h3>
              <div className={styles['panel-desc']}>
                <P>
                  {i18n.gettext('Get yours now - available on the iOS and Android app stores!')}
                </P>
              </div>
              <p className={styles.buttons}>
                <a
                  href="/app?platform=ios"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={clickAppDownload}
                >
                  <AppStoreBadge
                    type="ios"
                    text={i18n.gettext('Download Deliveroo in the App Store')}
                  />
                </a>
                <a
                  href="/app?platform=android"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={clickAppDownload}
                >
                  <AppStoreBadge
                    type="android"
                    text={i18n.gettext('Get Deliveroo on Google Play')}
                  />
                </a>
              </p>
            </div>
          </li>
        </ul>
      </Container>
    </div>
  );
};

HomepageMarketingContent.propTypes = {
  config: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  currentCountry: PropTypes.shape({
    tld: PropTypes.string.isRequired,
    enableDeliverooForBusiness: PropTypes.bool.isRequired,
  }).isRequired,
  rooviteLink: PropTypes.string,
  clickInviteLink: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  clickAppDownload: PropTypes.func,
};

HomepageMarketingContent.defaultProps = {
  rooviteLink: null,
  clickAppDownload: () => {},
};

export default connect(
  (state) => ({
    config: state.config,
    currentCountry: state.request.country,
    locale: state.request.locale,
    rooviteLink: state.user.roovite.link,
  }),
  (dispatch) => ({
    clickInviteLink: () => dispatch(showInviteModal()),
    clickAppDownload: () => dispatch(trackClickAppDownload('homepage')),
  }),
)(HomepageMarketingContent);
